// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import type { TyWarrantyRecord, TyWarrantyState } from "@/types";
// --- Logic --------------------------------------------------------------------------------------
/**
 * Where a guarantee stands today, read off its end date and nothing else.
 *
 * The collection stores no status, so this is the only place the four words are decided — and the
 * backend's `state` filter cuts the same list by the same windows, mirrored through
 * `constants.warrantyExpiringDays`. A record whose end date nobody filled in is `unknown` rather
 * than active: the honest answer to "is this still covered" is that we do not know.
 */
export function warrantyState(warranty: Pick<TyWarrantyRecord, "endsAt">): TyWarrantyState {
    if (!warranty.endsAt) return "unknown";

    const ends = new Date(warranty.endsAt);
    if (Number.isNaN(ends.getTime())) return "unknown";

    const now = new Date();
    if (ends < now) return "expired";

    const soon = new Date(now.getTime() + constants.warrantyExpiringDays * 24 * 60 * 60 * 1000);
    return ends <= soon ? "expiring" : "active";
}

/**
 * How much of the cover is spent, from 0 to 1 — `null` when the dates cannot say.
 *
 * It lives here rather than in the popup that draws it for one reason: reading the clock is impure,
 * and a component that calls `Date.now()` while rendering is a component React may render twice and
 * get two answers from.
 */
export function warrantyElapsed(warranty: Pick<TyWarrantyRecord, "startsAt" | "endsAt">): number | null {
    if (!warranty.startsAt || !warranty.endsAt) return null;

    const starts = new Date(warranty.startsAt).getTime();
    const ends = new Date(warranty.endsAt).getTime();
    if (Number.isNaN(starts) || Number.isNaN(ends) || ends <= starts) return null;

    return Math.min(Math.max((Date.now() - starts) / (ends - starts), 0), 1);
}

/** How long is left, in whole days. Negative once it has run out, which the card never prints. */
export function warrantyDaysLeft(warranty: Pick<TyWarrantyRecord, "endsAt">): number | null {
    if (!warranty.endsAt) return null;

    const ends = new Date(warranty.endsAt);
    if (Number.isNaN(ends.getTime())) return null;

    return Math.ceil((ends.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}
